/**
 * Effets de défilement de la page d'accueil (framer-motion).
 *  - ParallaxSlide : la section « glisse » en place en entrant dans l'écran
 *    (montée, léger zoom, fondu), puis s'efface un peu en sortant par le haut.
 *  - useHeroParallax : le hero recule pendant le défilement (contenu qui monte
 *    et s'estompe, filigrane qui défile plus lentement).
 * Désactivé si l'utilisateur demande moins d'animations.
 */
import {
  motion,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
  type MotionValue
} from 'framer-motion';
import { useRef, type ReactNode, type RefObject } from 'react';

const SPRING = { stiffness: 120, damping: 24, mass: 0.4 };

export function ParallaxSlide({
  children,
  depth = 90
}: {
  children: ReactNode;
  depth?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start']
  });
  const progress = useSpring(scrollYProgress, SPRING);
  // 0 → 0.35 : entrée ; 0.35 → 0.75 : en place ; 0.75 → 1 : sortie par le haut
  const y = useTransform(progress, [0, 0.35, 0.75, 1], [depth, 0, 0, -depth * 0.35]);
  const scale = useTransform(progress, [0, 0.35, 0.75, 1], [0.94, 1, 1, 0.98]);
  const opacity = useTransform(progress, [0, 0.25, 0.8, 1], [0.15, 1, 1, 0.6]);

  if (reduce) return <div ref={ref}>{children}</div>;
  return (
    <div ref={ref} className="relative">
      <motion.div style={{ y, scale, opacity, willChange: 'transform' }}>
        {children}
      </motion.div>
    </div>
  );
}

export function useHeroParallax(ref: RefObject<HTMLElement>): {
  contentY: MotionValue<number> | number;
  contentOpacity: MotionValue<number> | number;
  watermarkY: MotionValue<number> | number;
  watermarkScale: MotionValue<number> | number;
} {
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end start']
  });
  const progress = useSpring(scrollYProgress, SPRING);
  const contentY = useTransform(progress, [0, 1], [0, 160]);
  const contentOpacity = useTransform(progress, [0, 0.7], [1, 0]);
  const watermarkY = useTransform(progress, [0, 1], [0, 260]);
  const watermarkScale = useTransform(progress, [0, 1], [1, 1.25]);
  if (reduce)
    return { contentY: 0, contentOpacity: 1, watermarkY: 0, watermarkScale: 1 };
  return { contentY, contentOpacity, watermarkY, watermarkScale };
}
